import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// `ask_user_question` interactive answer form (fn-48.4, R7/R10) — the
// FIRST interactive answer surface anywhere in the platform UI (the
// operator card punts elicitation rows to the sbp CLI).
//
// **STRICT parser, not the transcript one.** `parseAskUserInput`
// (internal/transcript/ask-user.tsx) is deliberately forgiving — it
// skips malformed questions for read-only summaries. That is UNSAFE
// here: the submitted `formData` must echo `{ questions, answers }`
// back through the CP's `validateAskUserAnswers`
// (packages/hitl/src/custom-tools/ask-user-question.ts), and a form
// that silently dropped a question would submit an answers map the
// server rejects — or worse, answer a different form than the agent
// asked. `parseAskUserFormInput` therefore enforces the full tool-def
// contract (1-4 questions; 2-4 options each; non-empty unique
// question texts and option labels; header ≤12 chars) and on ANY
// violation the caller renders a non-actionable friendly fallback.
//
// **Client-side validation mirrors the server rules** (every question
// answered; multiSelect ≥1 selection; "Other" free-text non-empty
// trimmed; no duplicate picks) with inline per-question hints —
// submit is blocked client-side rather than surfacing a 400. The
// server's validator stays the source of truth; this mirror is UX,
// not security.
//
// **"Other" free-text** follows the Agent SDK convention (docs/
// learnings/2026-05-18): the typed value IS the answer — sent
// verbatim (trimmed) as the label string, no marker.
//
// **`readOnly`** renders the questions as informational text only —
// no inputs, no submit, zero `decideApproval` calls.
import { useEffect, useId, useRef, useState } from 'react';
import { DecisionError } from '../../adapter.js';
import { usePlatformAdapter } from '../context.js';
import { ANSWERS_SENT, ASK_USER_FALLBACK_BODY, ASK_USER_FALLBACK_TITLE, ASK_USER_HINTS, DECISION_SENDING, ERROR_COPY, OTHER_OPTION_LABEL, OTHER_TEXT_PLACEHOLDER, SUBMIT_ANSWERS_LABEL, asksYou, } from '../copy/copy.js';
/** Canonical elicitation tool name (mirrors packages/hitl). */
export const ASK_USER_TOOL_NAME = 'ask_user_question';
/**
 * Non-actionable fallback card — rendered for malformed ask-user
 * payloads AND for any other `custom_tool_result_immediate` approval
 * (immediate acks require form data this UI cannot synthesize; a bare
 * approval card would invite an Approve that the CP rejects with 400).
 */
export function AskUserFallbackCard() {
    return (_jsxs("div", { className: "sbui-action-card sbui-askform-fallback", "data-testid": "sbui-askform-fallback", children: [_jsx("h3", { className: "sbui-action-card-heading", children: ASK_USER_FALLBACK_TITLE }), _jsx("p", { className: "sbui-action-card-body", children: ASK_USER_FALLBACK_BODY })] }));
}
// ── Strict payload parser ───────────────────────────────────────────
const MAX_QUESTIONS = 4;
const MIN_OPTIONS = 2;
const MAX_OPTIONS = 4;
const MAX_HEADER_CHARS = 12;
/**
 * STRICT parse of an `ask_user_question` tool input for the
 * interactive form. Returns null on ANY contract violation — the
 * caller must render the non-actionable fallback, never a partial
 * form. Mirrors the question-shape rules of the CP's
 * `validateAskUserAnswers` plus the tool-def schema bounds.
 */
export function parseAskUserFormInput(input) {
    // The source is typed Record upstream, but adapters can violate that
    // at runtime (tool_args: null / array / scalar off the wire) — a
    // malformed payload must yield the fallback card, never a throw.
    if (input === null || typeof input !== 'object' || Array.isArray(input))
        return null;
    const qs = input.questions;
    if (!Array.isArray(qs))
        return null;
    if (qs.length === 0 || qs.length > MAX_QUESTIONS)
        return null;
    const out = [];
    const seenQuestions = new Set();
    for (const q of qs) {
        if (q === null || typeof q !== 'object' || Array.isArray(q))
            return null;
        const qq = q;
        if (typeof qq.question !== 'string' || qq.question.length === 0)
            return null;
        if (seenQuestions.has(qq.question))
            return null;
        seenQuestions.add(qq.question);
        if (qq.header !== undefined &&
            (typeof qq.header !== 'string' || qq.header.length > MAX_HEADER_CHARS)) {
            return null;
        }
        if (qq.multiSelect !== undefined && typeof qq.multiSelect !== 'boolean')
            return null;
        if (!Array.isArray(qq.options))
            return null;
        if (qq.options.length < MIN_OPTIONS || qq.options.length > MAX_OPTIONS)
            return null;
        const options = [];
        const seenLabels = new Set();
        for (const o of qq.options) {
            if (o === null || typeof o !== 'object' || Array.isArray(o))
                return null;
            const oo = o;
            if (typeof oo.label !== 'string' || oo.label.length === 0)
                return null;
            if (seenLabels.has(oo.label))
                return null;
            seenLabels.add(oo.label);
            if (oo.description !== undefined && typeof oo.description !== 'string')
                return null;
            options.push({
                label: oo.label,
                ...(oo.description !== undefined ? { description: oo.description } : {}),
            });
        }
        out.push({
            question: qq.question,
            options,
            ...(qq.header !== undefined ? { header: qq.header } : {}),
            ...(qq.multiSelect !== undefined ? { multiSelect: qq.multiSelect } : {}),
        });
    }
    return out;
}
const EMPTY_DRAFT = { picks: [], other: false, otherText: '' };
/**
 * Per-question validation hint, mirroring the server rules: every
 * question answered; multiSelect ≥1; "Other" text non-empty trimmed;
 * no duplicate picks (Other text colliding with a chosen label).
 * Null when the draft is a valid answer.
 */
function draftHint(question, draft) {
    const multi = question.multiSelect === true;
    const otherText = draft.otherText.trim();
    if (multi) {
        if (draft.picks.length === 0 && !draft.other)
            return ASK_USER_HINTS.chooseAtLeastOne;
        if (draft.other && otherText.length === 0)
            return ASK_USER_HINTS.otherTextRequired;
        if (draft.other && draft.picks.includes(otherText))
            return ASK_USER_HINTS.duplicateOther;
        return null;
    }
    if (draft.other) {
        return otherText.length === 0 ? ASK_USER_HINTS.otherTextRequired : null;
    }
    return draft.picks.length === 0 ? ASK_USER_HINTS.chooseOne : null;
}
/** Answer value for a VALID draft (`draftHint` returned null). */
function draftAnswer(question, draft) {
    const otherText = draft.otherText.trim();
    if (question.multiSelect === true) {
        return [...draft.picks, ...(draft.other ? [otherText] : [])];
    }
    return draft.other ? otherText : (draft.picks[0] ?? '');
}
export function AskUserForm(props) {
    const adapter = usePlatformAdapter();
    const headingId = useId();
    const fieldPrefix = useId();
    const containerRef = useRef(null);
    // Map, NOT a plain object: keys are agent-provided question texts, and
    // a plain object would resolve "toString"/"__proto__" to inherited
    // properties (crash) or mutate the prototype (pollution).
    const [drafts, setDrafts] = useState(new Map());
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [phase, setPhase] = useState('idle');
    const [failed, setFailed] = useState(false);
    const { approval, readOnly, source } = props;
    // Parse on every render (cheap, bounded at 4×4); the SOURCE object
    // identity may churn per poll while content stays identical.
    const questions = parseAskUserFormInput(source);
    const renderable = questions !== null;
    // Prompt focus treatment: the container, never a control — a stray
    // Enter must not submit a half-filled form. Skip in readOnly. The
    // boolean dep (not `questions`) keeps poll-driven array identity
    // churn from re-stealing focus mid-fill.
    useEffect(() => {
        if (!readOnly && renderable)
            containerRef.current?.focus();
    }, [readOnly, renderable]);
    // ── Non-actionable fallback: NEVER a partial form ────────────────
    if (questions === null) {
        return _jsx(AskUserFallbackCard, {});
    }
    const heading = asksYou(props.agentName, questions.length);
    // ── readOnly: informational text only ────────────────────────────
    if (readOnly) {
        return (_jsxs("div", { className: "sbui-action-card sbui-askform", "data-testid": "sbui-askform-readonly", children: [_jsx("h3", { className: "sbui-action-card-heading", children: heading }), _jsx("ol", { className: "sbui-askform-readonly-list", children: questions.map((q) => (_jsxs("li", { children: [q.header !== undefined && q.header.length > 0 && (_jsx("span", { className: "sbui-askform-header", children: q.header })), _jsx("span", { className: "sbui-askform-question", children: q.question }), _jsx("ul", { className: "sbui-askform-readonly-options", children: q.options.map((o) => (_jsx("li", { children: o.label }, o.label))) })] }, q.question))) })] }));
    }
    const draftFor = (text) => drafts.get(text) ?? EMPTY_DRAFT;
    const setDraft = (text, update) => {
        setDrafts((prev) => {
            const next = new Map(prev);
            next.set(text, update(prev.get(text) ?? EMPTY_DRAFT));
            return next;
        });
    };
    const hints = new Map(questions.map((q) => [q.question, draftHint(q, draftFor(q.question))]));
    const allValid = [...hints.values()].every((h) => h === null);
    const working = phase === 'working';
    const settled = phase === 'sent' || phase === 'conflicted';
    const submit = async () => {
        if (approval === null || working || settled)
            return;
        if (!allValid) {
            // Block client-side; the hints render inline per question.
            setSubmitAttempted(true);
            return;
        }
        // Null-prototype object: a question text of "__proto__" must land
        // as an OWN property (a plain literal would silently mutate the
        // prototype and drop the answer — the CP validator would then 400
        // on the missing key). JSON-serializes identically.
        const answers = Object.create(null);
        for (const q of questions) {
            answers[q.question] = draftAnswer(q, draftFor(q.question));
        }
        setPhase('working');
        setFailed(false);
        try {
            await adapter.decideApproval({
                sessionId: approval.session_id,
                approvalId: approval.id,
                decision: 'approved',
                // `questions` echoed VERBATIM from the source payload — the CP
                // validator cross-references answers against this exact array.
                formData: { questions: source.questions, answers },
            });
            // Pessimistic success: the sent note holds until the stream ack
            // clears the gate (the parent unmounts this form).
            setPhase('sent');
            props.onDecided?.();
        }
        catch (err) {
            const kind = err instanceof DecisionError ? err.kind : 'other';
            if (kind === 'conflict') {
                setPhase('conflicted');
                props.onRequestRefresh?.();
            }
            else {
                setPhase('idle');
                setFailed(true);
            }
        }
    };
    return (_jsxs("form", { ref: containerRef, className: "sbui-action-card sbui-askform", "data-testid": "sbui-askform", "aria-labelledby": headingId, tabIndex: -1, onSubmit: (e) => {
            e.preventDefault();
            void submit();
        }, children: [_jsx("h3", { id: headingId, className: "sbui-action-card-heading", children: heading }), questions.map((q, qi) => {
                const multi = q.multiSelect === true;
                const draft = draftFor(q.question);
                const hint = hints.get(q.question) ?? null;
                const groupName = `${fieldPrefix}-q${qi}`;
                return (_jsxs("fieldset", { className: "sbui-askform-fieldset", disabled: working || settled, children: [_jsxs("legend", { className: "sbui-askform-legend", children: [q.header !== undefined && q.header.length > 0 && (_jsx("span", { className: "sbui-askform-header", children: q.header })), _jsx("span", { className: "sbui-askform-question", children: q.question })] }), q.options.map((o) => (_jsxs("label", { className: "sbui-askform-option", children: [_jsx("input", { type: multi ? 'checkbox' : 'radio', name: groupName, value: o.label, checked: draft.picks.includes(o.label), onChange: () => setDraft(q.question, (prev) => multi
                                        ? {
                                            ...prev,
                                            picks: prev.picks.includes(o.label)
                                                ? prev.picks.filter((p) => p !== o.label)
                                                : [...prev.picks, o.label],
                                        }
                                        : { ...prev, picks: [o.label], other: false }) }), _jsxs("span", { className: "sbui-askform-option-text", children: [_jsx("span", { className: "sbui-askform-option-label", children: o.label }), o.description !== undefined && o.description.length > 0 && (_jsx("span", { className: "sbui-askform-option-description", children: o.description }))] })] }, o.label))), _jsxs("label", { className: "sbui-askform-option", children: [_jsx("input", { type: multi ? 'checkbox' : 'radio', name: groupName, value: OTHER_OPTION_LABEL, checked: draft.other, onChange: () => setDraft(q.question, (prev) => multi ? { ...prev, other: !prev.other } : { ...prev, picks: [], other: true }) }), _jsx("span", { className: "sbui-askform-option-text", children: _jsx("span", { className: "sbui-askform-option-label", children: OTHER_OPTION_LABEL }) })] }), draft.other && (_jsx("input", { type: "text", className: "sbui-askform-other-input", placeholder: OTHER_TEXT_PLACEHOLDER, "aria-label": `${q.question} — ${OTHER_OPTION_LABEL}`, value: draft.otherText, onChange: (e) => {
                                const value = e.currentTarget.value;
                                setDraft(q.question, (prev) => ({ ...prev, otherText: value }));
                            } })), submitAttempted && hint !== null && (_jsx("p", { className: "sbui-askform-hint", "data-testid": "sbui-askform-hint", children: hint }))] }, q.question));
            }), phase === 'sent' ? (_jsx("output", { className: "sbui-action-card-decided", "data-testid": "sbui-askform-sent", children: ANSWERS_SENT })) : phase === 'conflicted' ? (_jsx("output", { className: "sbui-action-card-conflict", "data-testid": "sbui-action-card-conflict", children: ERROR_COPY.conflict })) : (_jsx("div", { className: "sbui-action-card-actions", children: _jsx("button", { type: "submit", className: "sbui-action-card-approve", disabled: working, children: working ? DECISION_SENDING : SUBMIT_ANSWERS_LABEL }) })), failed && (_jsx("p", { className: "sbui-action-card-error", role: "alert", children: ERROR_COPY.decision }))] }));
}
//# sourceMappingURL=ask-user-form.js.map